import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";

const Navbar = () => {
  const { lang, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const aboutDropdownRef = useRef(null);
  const servicesDropdownRef = useRef(null);
  const infoDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeDropdown &&
        aboutDropdownRef.current && !aboutDropdownRef.current.contains(event.target) &&
        servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target) &&
        infoDropdownRef.current && !infoDropdownRef.current.contains(event.target)
      ) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  const handleNavigateToHero = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    setActiveDropdown(null);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleScrollToSection = (id) => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
    if (location.pathname === "/") {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  return (
    <div className="fixed top-8 sm:top-10 md:top-12 left-0 right-0 z-50 px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 w-full flex justify-center pointer-events-none">
      <nav className="w-full max-w-[1200px] backdrop-blur-md bg-white/85 dark:bg-[#072227]/90 border border-white/40 dark:border-white/10 rounded-full px-3.5 sm:px-6 py-2 md:py-3 shadow-lg flex justify-between items-center pointer-events-auto transition-all duration-300">

        <a
          href="/"
          onClick={handleNavigateToHero}
          className="flex items-center space-x-2 sm:space-x-3 hover:opacity-90 transition-opacity min-w-0"
        >
          <img
            src="/logos/clinic_logo_icon.png"
            alt="Dr. Krishna's Dental Logo"
            className="h-9 w-9 sm:h-12 sm:w-12 object-contain flex-shrink-0"
          />
          <div className="flex flex-col items-start leading-tight min-w-0">
            <span className="font-serif font-extrabold text-xs sm:text-lg md:text-xl text-[#0A2E36] dark:text-white tracking-tight">
              Dr. Krishna's <br className="sm:hidden" />
              <span className="text-[#E09F3E] dark:text-[#F4B942] font-sans font-black italic">Dental</span>
            </span>
            {/* Green pill badge - Shown only on desktop/tablet */}
            <span className="hidden md:inline-block bg-[#56b255] text-white text-[9px] font-bold tracking-wider px-2 py-0.5 rounded-full mt-0.5 uppercase shadow-sm">
              MDS Specialties
            </span>
          </div>
        </a>

        {/* Center: Desktop Navigation Links */}
        <div className="hidden lg:flex items-center space-x-6 xl:space-x-8 text-[#0A2E36] dark:text-white/90 font-semibold text-sm">
          {/* Home */}
          <a
            href="/"
            onClick={handleNavigateToHero}
            className="hover:text-[#56b255] dark:hover:text-[#56b255] transition-colors py-2"
          >
            Home
          </a>

          {/* About Us Dropdown */}
          <div className="relative" ref={aboutDropdownRef}>
            <button
              onClick={() => toggleDropdown("about")}
              className={`flex items-center space-x-1 hover:text-[#56b255] dark:hover:text-[#56b255] transition-colors py-2 focus:outline-none ${activeDropdown === "about" ? "text-[#56b255]" : ""
                }`}
            >
              <span>About Us</span>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transform transition-transform duration-200 ${activeDropdown === "about" ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeDropdown === "about" && (
              <div className="absolute left-0 mt-2 w-48 bg-white/95 dark:bg-[#0B252B]/95 backdrop-blur-md border border-gray-150 dark:border-white/10 rounded-2xl shadow-xl p-2 space-y-1 z-50">
                <button
                  onClick={() => handleScrollToSection("about")}
                  className="w-full text-left text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Meet Specialists
                </button>
                <button
                  onClick={() => handleScrollToSection("about")}
                  className="w-full text-left text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Our Philosophy
                </button>
              </div>
            )}
          </div>

          {/* Services Dropdown */}
          <div className="relative" ref={servicesDropdownRef}>
            <button
              onClick={() => toggleDropdown("services")}
              className={`flex items-center space-x-1 hover:text-[#56b255] dark:hover:text-[#56b255] transition-colors py-2 focus:outline-none ${activeDropdown === "services" ? "text-[#56b255]" : ""
                }`}
            >
              <span>Services</span>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transform transition-transform duration-200 ${activeDropdown === "services" ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeDropdown === "services" && (
              <div className="absolute left-0 mt-2 w-56 bg-white/95 dark:bg-[#0B252B]/95 backdrop-blur-md border border-gray-150 dark:border-white/10 rounded-2xl shadow-xl p-2 space-y-1 z-50">
                <Link
                  to="/services/orthodontics"
                  onClick={() => setActiveDropdown(null)}
                  className="block text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Orthodontics
                </Link>
                <Link
                  to="/services/wisdom-teeth-removal"
                  onClick={() => setActiveDropdown(null)}
                  className="block text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Maxillofacial Surgery
                </Link>
                <Link
                  to="/services/cosmetic-dentistry"
                  onClick={() => setActiveDropdown(null)}
                  className="block text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Cosmetic Dentistry
                </Link>
                <Link
                  to="/services/general-dentistry"
                  onClick={() => setActiveDropdown(null)}
                  className="block text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  General Care
                </Link>
                <button
                  onClick={() => handleScrollToSection("services")}
                  className="w-full text-left text-xs font-bold text-[#E09F3E] dark:text-[#F4B942] hover:bg-gray-50 dark:hover:bg-[#10373E] px-3.5 py-2.5 rounded-xl border-t border-gray-100 dark:border-white/10 transition-all mt-1"
                >
                  View All Services →
                </button>
              </div>
            )}
          </div>

          {/* Patient Info Dropdown */}
          <div className="relative" ref={infoDropdownRef}>
            <button
              onClick={() => toggleDropdown("info")}
              className={`flex items-center space-x-1 hover:text-[#56b255] dark:hover:text-[#56b255] transition-colors py-2 focus:outline-none ${activeDropdown === "info" ? "text-[#56b255]" : ""
                }`}
            >
              <span>Patient Info</span>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transform transition-transform duration-200 ${activeDropdown === "info" ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {activeDropdown === "info" && (
              <div className="absolute left-0 mt-2 w-52 bg-white/95 dark:bg-[#0B252B]/95 backdrop-blur-md border border-gray-150 dark:border-white/10 rounded-2xl shadow-xl p-2 space-y-1 z-50">
                <button
                  onClick={() => handleScrollToSection("reviews")}
                  className="w-full text-left text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Google Reviews
                </button>
                <a
                  href="tel:+916281785274"
                  className="block text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Call Clinic: Dr. Sai Krishna
                </a>
                <a
                  href="tel:+918186871685"
                  className="block text-xs font-semibold text-gray-700 dark:text-slate-200 hover:text-white hover:bg-[#56b255] px-3.5 py-2.5 rounded-xl transition-all"
                >
                  Call Clinic: Dr. Thejasri
                </a>
              </div>
            )}
          </div>

          {/* Contact */}
          <Link
            to="/contact"
            className="hover:text-[#56b255] dark:hover:text-[#56b255] transition-colors py-2"
          >
            Contact
          </Link>
        </div>

        {/* Right: CTA, Language Toggle, Theme Toggle & Mobile Hamburger */}
        <div className="flex items-center space-x-1.5 sm:space-x-2.5 flex-shrink-0">
          {/* Language Toggle Button */}
          <button
            onClick={toggleLanguage}
            className="hidden sm:flex items-center space-x-1 bg-white/90 dark:bg-[#0B252B]/95 hover:bg-white dark:hover:bg-[#10373E] text-[#0A2E36] dark:text-white text-[11px] sm:text-xs font-bold px-2 sm:px-3 py-1.5 md:py-2 rounded-full border border-gray-200 dark:border-white/10 transition-all pointer-events-auto shadow-sm whitespace-nowrap"
            title="Switch Language"
          >
            <span className="text-xs sm:text-sm">🌐</span>
            <span className="font-sans font-extrabold">{lang === "en" ? "తెలుగు" : "ENG"}</span>
          </button>

          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center bg-white/90 dark:bg-[#0B252B]/95 hover:bg-white dark:hover:bg-[#10373E] text-[#0A2E36] dark:text-yellow-400 p-2 sm:p-2.5 rounded-full border border-gray-200 dark:border-white/10 transition-all pointer-events-auto shadow-sm"
            title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
          >
            {theme === "light" ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m0 13.5V21m9-9h-2.25C18.36 12 12 18.36 12 21M3 12h2.25m9-9h-2.25M6.343 6.343l1.591 1.591m9.068 9.068l1.591 1.591M17.657 6.343l-1.591 1.591M6.343 17.657l-1.591 1.591M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
              </svg>
            )}
          </button>

          {/* Green Book Appointment Button */}
          <Link
            to="/contact"
            className="bg-[#56b255] hover:bg-[#49a048] text-white text-xs md:text-sm font-bold px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full shadow-sm hover:shadow transition-all duration-200 whitespace-nowrap pointer-events-auto"
          >
            <span className="hidden xl:inline">{t.nav.bookAppointment}</span>
            <span className="xl:hidden">Book</span>
          </Link>

          {/* Mobile Menu Icon */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-[#0A2E36] dark:text-white hover:text-[#56b255] p-1.5 focus:outline-none transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Drawer Menu Panel */}
      {isMenuOpen && (
        <div className="absolute top-[70px] left-4 right-4 bg-white/95 dark:bg-[#0B252B]/95 backdrop-blur-lg border border-white/40 dark:border-white/10 shadow-2xl rounded-3xl p-6 flex flex-col space-y-4 lg:hidden pointer-events-auto animate-[fadeIn_0.2s_ease-out]">
          
          {/* Mobile Language Switcher Row */}
          <div className="flex items-center justify-between bg-gray-50 dark:bg-[#0A2E36]/30 border border-gray-200/80 dark:border-white/10 p-3 rounded-2xl">
            <span className="text-xs font-bold text-[#0A2E36] dark:text-white flex items-center space-x-2">
              <span>🌐</span>
              <span>{lang === "en" ? "Language Options" : "భాష ఎంపికలు"}</span>
            </span>
            <button
              onClick={toggleLanguage}
              className="bg-[#0F4C5C] dark:bg-[#E09F3E] text-white dark:text-[#0A2E36] text-xs font-bold px-3 py-1.5 rounded-full shadow-sm hover:bg-[#0c3c49] dark:hover:bg-[#c98e35] transition-all"
            >
              {lang === "en" ? "తెలుగుకి మారండి" : "Switch to English"}
            </button>
          </div>

          <a
            href="/"
            onClick={handleNavigateToHero}
            className="text-lg font-bold text-[#0A2E36] dark:text-white hover:text-[#56b255] py-2 border-b border-gray-100 dark:border-white/5"
          >
            Home
          </a>

          {/* About us mobile */}
          <div className="flex flex-col space-y-2 border-b border-gray-100 dark:border-white/5 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#E09F3E] dark:text-[#F4B942]">About Us</span>
            <button
              onClick={() => handleScrollToSection("about")}
              className="text-left text-md font-semibold text-gray-700 dark:text-gray-200 hover:text-[#56b255] pl-2"
            >
              Meet Specialists / Philosophy
            </button>
          </div>

          {/* Services mobile */}
          <div className="flex flex-col space-y-2 border-b border-gray-100 dark:border-white/5 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#E09F3E] dark:text-[#F4B942]">Our Services</span>
            <div className="grid grid-cols-2 gap-2 pl-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
              <Link to="/services/orthodontics" onClick={() => setIsMenuOpen(false)}>Orthodontics</Link>
              <Link to="/services/wisdom-teeth-removal" onClick={() => setIsMenuOpen(false)}>Oral Surgery</Link>
              <Link to="/services/cosmetic-dentistry" onClick={() => setIsMenuOpen(false)}>Cosmetic Care</Link>
              <Link to="/services/general-dentistry" onClick={() => setIsMenuOpen(false)}>General Care</Link>
            </div>
            <button
              onClick={() => handleScrollToSection("services")}
              className="text-left text-xs font-bold text-[#56b255] pl-2 pt-1"
            >
              See All Services →
            </button>
          </div>

          {/* Patient Info mobile */}
          <div className="flex flex-col space-y-2 border-b border-gray-100 dark:border-white/5 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#E09F3E] dark:text-[#F4B942]">Patient Support</span>
            <button
              onClick={() => handleScrollToSection("reviews")}
              className="text-left text-md font-semibold text-gray-700 dark:text-gray-200 hover:text-[#56b255] pl-2"
            >
              Google Reviews
            </button>
            <div className="flex flex-col space-y-1 pl-2 text-sm font-semibold text-gray-600 dark:text-gray-300">
              <a href="tel:+916281785274">📞 Call Dr. Sai Krishna</a>
              <a href="tel:+918186871685">📞 Call Dr. Thejasri</a>
            </div>
          </div>

          <Link
            to="/contact"
            onClick={() => setIsMenuOpen(false)}
            className="text-lg font-bold text-[#0A2E36] dark:text-white hover:text-[#56b255] py-2"
          >
            Contact Us
          </Link>
        </div>
      )}
    </div>
  );
};

export default Navbar;
